import { redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { authResponseToSessionData } from './utils';

import { sessionMiddleware } from '@/middleware/session';
import type { TLAIRefreshTokenResponse } from '@/types/api';
import { apiClient } from '@/utils/api-client';
import { createFormData, extractHTTPError } from '@/utils/http-client';
import { safeInvoke } from '@/utils/safe-invoke';
import { updateSessionDataHeaders } from '@/utils/session';

export const refreshServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionMiddleware])
  .inputValidator(z.object({ redirectTo: z.string().optional() }).optional())
  .handler(async ({ data, context: { session } }) => {
    if (!session.authentication) {
      throw redirect({
        to: '/login',
        search: { redirectTo: data?.redirectTo },
      });
    }

    const apiClientInstance = await apiClient({ authenticated: false });

    const { refreshToken, refreshExpiresAt } = session.authentication;

    if (!refreshToken || !refreshExpiresAt || refreshExpiresAt <= Date.now()) {
      console.info(
        `[REFRESH] ${refreshToken ? 'Refresh token expired' : 'Refresh token not found'}. Redirecting to login.`,
      );
      throw redirect({
        to: '/login',
        search: { redirectTo: data?.redirectTo },
        headers: updateSessionDataHeaders(new Headers(), { ...session, authentication: undefined }),
      });
    }

    const response = await safeInvoke(
      apiClientInstance.post<TLAIRefreshTokenResponse>(
        '/auth/refresh',
        createFormData({ refresh_token: refreshToken }),
      ),
    );

    if (response.error) {
      console.error(
        '[REFRESH] Error refreshing token:',
        extractHTTPError(response.error, 'An unexpected error occurred'),
      );
      throw response.error;
    }

    if (response.data) {
      return new Response(
        JSON.stringify({ expires_in: response.data.expires_in, refresh_expires_in: response.data.refresh_expires_in }),
        {
          headers: updateSessionDataHeaders(new Headers(), {
            ...session,
            ...authResponseToSessionData(response.data),
          }),
        },
      );
    }

    return new Response('No data returned from refresh token endpoint', { status: 500 });
  });
